import React, { useContext, useEffect } from 'react';
import { Card, DatePicker, Form, Input, Select, message } from 'antd';

import { FormCard, FormSectionTitle, FormContainer } from '@leedpulse-monorepo/components';
import { CONTACTS_PAGE_PATH } from '../../pages/contacts/ContactsPage';
import ContactCreationFormContext from '../../contexts/forms/contacts/ContactCreationFormContext';
import { SUBSCRIPTIONS_PAGE_PATH } from '../../pages/subscriptions/SubscriptionsPage';
import { getSenderCountryFlag } from "@leedpulse-monorepo/utils";
import { ContextStatus, channelAsString, EContactCivility, contactCivilityAsString } from "@leedpulse-monorepo/enums";
import NumbersContext from '../../contexts/data/NumbersContext';
import { handleErrorInputMessage, handleInputError } from '../../../core/utils/errorUtils';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Scenario from '../../../domain/models/Scenario';


const { TextArea } = Input;
interface ContactCreationFormProps {
  subscriptionId: string;
  globalScenarios?: Scenario[];
}

const ContactCreationForm: React.FC<ContactCreationFormProps> = ({
  subscriptionId,
  globalScenarios = [],
}) => {
  const navigate = useNavigate();
  const formContext = useContext(ContactCreationFormContext);
  const numbersContext = useContext(NumbersContext);

  useEffect(() => {
    console.log("initWithSubscriptionId",subscriptionId);
    formContext.initWithSubscriptionId(subscriptionId, globalScenarios);
  }, []);

  useEffect(() => {
    if (formContext.subscription) {
      numbersContext.fetchFromSenders(formContext.subscription.sender);
    }
  }, [formContext.subscription]);

  useEffect(() => {
    switch (formContext.status) {
      case ContextStatus.SUCCESS:
        message.success('Contact créé avec succès');
        navigate(
          SUBSCRIPTIONS_PAGE_PATH + '/' + subscriptionId + CONTACTS_PAGE_PATH
        );
        break;
      case ContextStatus.ERROR:
        message.error(formContext.errorMessage);
        break;
      default:
        break;
    }
  }, [formContext.status]);

  const isBusy = formContext.status === ContextStatus.LOADING;

  console.log("loading",formContext.subscription?.id !== subscriptionId);
  console.log("subscription.id",formContext.subscription?.id);
  console.log("subscriptionId",subscriptionId);
  return formContext.subscription?.id !== subscriptionId ? (
    <Card loading />
  ) : (
    <FormCard
      title="Nouveau contact"
      showTitle={false}
      onCreated={() => formContext.submit()}
      isCreateLoading={formContext.status === ContextStatus.LOADING}
    >
      <Form layout="vertical">
        <FormSectionTitle title="Informations du contact" />
        <FormContainer>
          <Form.Item label="Civilité">
            <Select
              value={formContext.civility}
              onChange={(value) => formContext.onCivilityChange(value as EContactCivility)}
              disabled={isBusy}
            >
              {Object.values(EContactCivility).map((civility) => (
                <Select.Option key={civility} value={civility}>
                  {contactCivilityAsString(civility)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Prénom"
            validateStatus={
              handleInputError(formContext.firstname, formContext.status)
            }
            help={
              handleErrorInputMessage(formContext.firstname, formContext.status)
            }>
            <Input
              value={formContext.firstname}
              onChange={(e) => formContext.onFirstnameChange(e.target.value)}
              disabled={isBusy}
            />
          </Form.Item>

          <Form.Item
            label="Nom"
            validateStatus={
              handleInputError(formContext.lastname, formContext.status)
            }
            help={
              handleErrorInputMessage(formContext.lastname, formContext.status)
            }>
            <Input
              value={formContext.lastname}
              onChange={(e) => formContext.onLastnameChange(e.target.value)}
              disabled={isBusy}
            />
          </Form.Item>

          <Form.Item
            label="Téléphone"
            validateStatus={
              handleInputError(formContext.phone, formContext.status)
            }
            help={
              handleErrorInputMessage(formContext.phone, formContext.status)
            }>
            <Input
              value={formContext.phone}
              onChange={(e) => {
                // Remove spaces
                e.target.value = e.target.value.replace(/\s/g, '');
                formContext.onPhoneChange(e.target.value)
              }}
              disabled={isBusy}
            />
          </Form.Item>

          <Form.Item label="Email">
            <Input
              value={formContext.email}
              onChange={(e) => formContext.onEmailChange(e.target.value)}
              disabled={isBusy}
            />
          </Form.Item>

          <Form.Item label="Entreprise">
            <Input
              value={formContext.company}
              onChange={(e) => formContext.onCompanyChange(e.target.value)}
              disabled={isBusy}
            />
          </Form.Item>

          <Form.Item label="Poste">
            <Input
              value={formContext.role}
              onChange={(e) => formContext.onRoleChange(e.target.value)}
              disabled={isBusy}
            />
          </Form.Item>

          <Form.Item label="Adresse">
            <Input
              value={formContext.adresse}
              onChange={(e) => formContext.onAdresseChange(e.target.value)}
              disabled={isBusy}
            />
          </Form.Item>

          <Form.Item label="Site web">
            <Input
              value={formContext.website}
              onChange={(e) => formContext.onWebsiteChange(e.target.value)}
              disabled={isBusy}
            />
          </Form.Item>

          <Form.Item label="Conseiller">
            <Select
              disabled={isBusy}
              onChange={(value) => {
                formContext.onOwnerChange(value as string);
              }}
              value={formContext.owner}
            >
              {formContext.subscriptionOwners.map((owner) => (
                <Select.Option key={owner.uid} value={owner.uid}>
                  {owner.firstname + ' ' + owner.lastname}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Scénario">
            <Select
              onChange={(value) =>
                formContext.onProspectCategoryChange(
                  value as string
                )
              }
              value={formContext.prospectCategory}
            >
              {formContext.subscriptionScenarios.map((scenario) => (
                <Select.Option key={scenario.id} value={scenario.id}>
                  {scenario.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Programmer la prise de contact">
            <Select
              value={formContext.contactScheduled}
              onChange={(value) => {
                formContext.onContactScheduledChange(value);
              }}
            >
              <Select.Option value={true}>Oui</Select.Option>
              <Select.Option value={false}>Non</Select.Option>
            </Select>
          </Form.Item>

          {formContext.contactScheduled && (
            <Form.Item label="Date de début de prise de contact">
              <DatePicker
                style={{ width: '100%' }}
                format={'DD/MM/YYYY'}
                value={formContext.contactScheduledDate || dayjs()}
                onChange={(date) => formContext.onContactScheduledDateChange(date)}
                allowClear={false}
                disabledDate={(current) => current && current < dayjs()}
              />
            </Form.Item>
          )}

          <Form.Item label="N° d'envoyeur">
            <Select
              disabled={isBusy}
              loading={numbersContext.sendersStatus === ContextStatus.LOADING}
              onChange={(value) => {
                // Remove spaces
                value = value.replace(/\s/g, '');
                formContext.onSenderChange(value as string);
              }}
              value={formContext.sender}
            >
              {numbersContext.items.map((number) => (
                <Select.Option key={number.sender} value={number.sender}>
                  {`${getSenderCountryFlag(number.sender)} ${number.sender} (${channelAsString(number.channel)}) - ${number.provider}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Détails">
            <TextArea
              value={formContext.details}
              onChange={(e) => formContext.onDetailsChange(e.target.value)}
              disabled={isBusy}
              showCount
              rows={4}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </Form.Item>
        </FormContainer>
      </Form>
    </FormCard>
  );
};

export default ContactCreationForm;
